import { useMemo, useCallback } from 'react'
import type { SchemaField } from '../../../types/schede'
import FieldRenderer from './FieldRenderer'
import SectionField from './fields/SectionField'

interface FormRendererProps {
  schema: SchemaField[]
  dati: Record<string, any>
  onChange: (fieldId: string, value: any) => void
  readOnly?: boolean
  errors?: Record<string, string>
}

/** Valuta se un campo e' visibile in base alle regole di visibilita' */
function isFieldVisible(field: SchemaField, dati: Record<string, any>): boolean {
  const rules = field.visibility_rules
  if (!rules || rules.length === 0) return true

  let result = true
  for (let i = 0; i < rules.length; i++) {
    const rule = rules[i]
    const actual = dati[rule.field_id]
    let match = false

    switch (rule.operator) {
      case 'eq': match = String(actual ?? '') === String(rule.value ?? ''); break
      case 'neq': match = String(actual ?? '') !== String(rule.value ?? ''); break
      case 'gt': match = parseFloat(actual) > parseFloat(rule.value); break
      case 'lt': match = parseFloat(actual) < parseFloat(rule.value); break
      case 'gte': match = parseFloat(actual) >= parseFloat(rule.value); break
      case 'lte': match = parseFloat(actual) <= parseFloat(rule.value); break
      case 'contains': match = String(actual ?? '').toLowerCase().includes(String(rule.value ?? '').toLowerCase()); break
      case 'empty': match = actual == null || actual === '' || (Array.isArray(actual) && actual.length === 0); break
      case 'not_empty': match = actual != null && actual !== '' && !(Array.isArray(actual) && actual.length === 0); break
    }

    if (i === 0) {
      result = match
    } else if (rule.logic === 'or') {
      result = result || match
    } else {
      result = result && match
    }
  }
  return result
}

/** Valuta formule di calcolo */
function evaluateCalculations(schema: SchemaField[], dati: Record<string, any>): Record<string, any> {
  const updated = { ...dati }

  for (const field of schema) {
    if (field.type !== 'calculated' || !field.calculation_formula) continue

    try {
      let formula = field.calculation_formula

      // Aggregati tabella: SUM({{table.col}})
      const aggRx = /(SUM|AVG|COUNT|MIN|MAX)\(\{\{(\w[\w-]*)\.(\w[\w-]*)\}\}\)/g
      let aggMatch
      while ((aggMatch = aggRx.exec(field.calculation_formula)) !== null) {
        const [full, func, tableId, colId] = aggMatch
        const tableData = updated[tableId]
        if (!Array.isArray(tableData)) { updated[field.id] = null; continue }

        const values = tableData
          .map((row: any) => parseFloat(row?.[colId]))
          .filter((v: number) => !isNaN(v))

        let aggResult = 0
        if (func === 'SUM') aggResult = values.reduce((a: number, b: number) => a + b, 0)
        else if (func === 'AVG') aggResult = values.length ? values.reduce((a: number, b: number) => a + b, 0) / values.length : 0
        else if (func === 'COUNT') aggResult = tableData.length
        else if (func === 'MIN') aggResult = values.length ? Math.min(...values) : 0
        else if (func === 'MAX') aggResult = values.length ? Math.max(...values) : 0

        formula = formula.replace(full, String(aggResult))
      }

      // Riferimenti semplici: {{field_id}}
      const refRx = /\{\{(\w[\w-]*)\}\}/g
      let refMatch
      let hasNull = false
      while ((refMatch = refRx.exec(formula)) !== null) {
        const refId = refMatch[1]
        const val = updated[refId]
        if (val == null || val === '') { hasNull = true; break }
        formula = formula.replace(refMatch[0], String(parseFloat(val)))
      }

      if (hasNull) {
        updated[field.id] = null
        continue
      }

      // Safe eval: solo numeri e operatori
      if (/^[\d\s\+\-\*\/\(\)\.]+$/.test(formula)) {
        const result = Function('"use strict"; return (' + formula + ')')()
        const precision = field.calculation_precision ?? 2
        updated[field.id] = Math.round(result * Math.pow(10, precision)) / Math.pow(10, precision)
      } else {
        updated[field.id] = null
      }
    } catch {
      updated[field.id] = null
    }
  }

  return updated
}

interface FieldGroup {
  section: SchemaField | null
  fields: SchemaField[]
}

/** Raggruppa campi in sezioni: campi dopo un 'section' appartengono a quella sezione.
 *  Campi con outside_section=true spezzano il gruppo e tornano top-level. */
function groupFieldsIntoSections(sortedFields: SchemaField[]): FieldGroup[] {
  const groups: FieldGroup[] = []
  let current: FieldGroup = { section: null, fields: [] }

  for (const field of sortedFields) {
    if (field.type === 'section') {
      if (current.section || current.fields.length > 0) {
        groups.push(current)
      }
      current = { section: field, fields: [] }
    } else if (field.outside_section && current.section) {
      // Campo sganciato dalla sezione: chiudi il gruppo corrente e inizia uno nuovo senza sezione
      groups.push(current)
      current = { section: null, fields: [field] }
    } else {
      current.fields.push(field)
    }
  }
  if (current.section || current.fields.length > 0) {
    groups.push(current)
  }

  return groups
}

export default function FormRenderer({ schema, dati, onChange, readOnly = false, errors = {} }: FormRendererProps) {
  const computedDati = useMemo(() => evaluateCalculations(schema, dati), [schema, dati])

  const handleChange = useCallback((fieldId: string, value: any) => {
    onChange(fieldId, value)
  }, [onChange])

  const sortedFields = useMemo(() =>
    [...schema].sort((a, b) => a.ordine - b.ordine),
    [schema]
  )

  const groups = useMemo(() => groupFieldsIntoSections(sortedFields), [sortedFields])

  const renderField = (field: SchemaField) => {
    if (!isFieldVisible(field, computedDati)) return null

    const value = field.type === 'calculated'
      ? computedDati[field.id]
      : (dati[field.id] ?? field.defaultValue ?? (field.type === 'table' ? [] : ''))

    const error = errors[field.id]
    const widthClass = field.width === 'half' ? 'w-1/2' : field.width === 'third' ? 'w-1/3' : 'w-full'

    return (
      <div key={field.id} className={`${widthClass} px-1 mb-3`}>
        <FieldRenderer
          field={field}
          value={value}
          onChange={(val) => handleChange(field.id, val)}
          readOnly={readOnly || field.type === 'calculated' || field.type === 'readonly'}
          error={error}
          allDati={computedDati}
        />
      </div>
    )
  }

  const renderFields = (fields: SchemaField[]) => (
    <div className="flex flex-wrap -mx-1">
      {fields.map(renderField)}
    </div>
  )

  return (
    <div>
      {groups.map((group, gi) => {
        if (group.section) {
          // Sezione con i suoi campi figli
          if (!isFieldVisible(group.section, computedDati)) return null
          return (
            <div key={group.section.id} className="w-full">
              <SectionField field={group.section}>
                {renderFields(group.fields)}
              </SectionField>
            </div>
          )
        }
        // Campi senza sezione (top-level)
        return (
          <div key={`group-${gi}`}>
            {renderFields(group.fields)}
          </div>
        )
      })}
    </div>
  )
}

export { isFieldVisible, evaluateCalculations }
